import { Component, Inject, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';
import { ReplaySubject } from 'rxjs';
import { IFLIForNotes } from '../../interfaces/IFLIForNotes.interface';

export type ReferencingNotesComponentData = {
    flis: IFLIForNotes[];
    selectedFlis: string[];
};
@UntilDestroy()
@Component({
    selector: 'referencing-notes',
    templateUrl: './referencing-notes.component.html',
    styleUrls: ['./referencing-notes.component.scss'],
})
export class ReferencingNotesComponent {
    filterCtrl: UntypedFormControl = new UntypedFormControl();
    filteredOptions: ReplaySubject<IFLIForNotes[]> = new ReplaySubject<IFLIForNotes[]>(1);
    selectedOptions: Map<string, IFLIForNotes> = new Map();

    currentSelectedItems: IFLIForNotes[] = [];
    filterText: string | undefined;

    @ViewChild(MatAutocompleteTrigger, { static: false }) autoCompleteTrigger: MatAutocompleteTrigger;

    constructor(
        public dialogRef: MatDialogRef<ReferencingNotesComponent, IFLIForNotes[]>,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public data: ReferencingNotesComponentData,
    ) {
        this.addSvgIcon('interface-delete 1');
        this.addSvgIcon('interface search');

        this.filteredOptions.next(this.data.flis.slice());

        this.initializeControlSubscription();

        if (data.selectedFlis != null) {
            this.initializePreselectedData(data.selectedFlis);
        }
    }

    initializePreselectedData(selectedFlis: string[]) {
        const flisMap = new Map<string, IFLIForNotes>();

        this.data.flis.forEach((fli) => {
            flisMap.set(fli.id, fli);
        });

        selectedFlis.forEach((fliId) => {
            const fli = flisMap.get(fliId);

            if (fli != null) {
                this.currentSelectedItems.push(fli);
                this.selectedOptions.set(fli.id, fli);
            }
        });
    }

    private initializeControlSubscription(): void {
        this.filterCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value: string | IFLIForNotes) => {
            if (typeof value === 'string') {
                const lowerCaseValue = value.toLowerCase();
                this.filterText = lowerCaseValue;
                this.filteredOptions.next(
                    this.data.flis.filter(
                        (item) => item.caption != null && item.caption.toLowerCase().indexOf(lowerCaseValue) > -1,
                    ),
                );
            }
        });
    }

    public onSaveClicked(): void {
        const itemSelections = Array.from(this.selectedOptions.values());
        this.dialogRef.close(itemSelections);
    }

    public closeDialog(): void {
        this.dialogRef.close();
    }

    public displayFn(value: string | IFLIForNotes | null): string {
        if (typeof value === 'string') {
            return value;
        }
        return this.filterText;
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    onOptionClicked(option: IFLIForNotes) {
        if (this.selectedOptions.has(option.id)) {
            this.selectedOptions.delete(option.id);
        } else {
            this.selectedOptions.set(option.id, option);
        }

        const clonedSelectedOptionsMap: Map<string, IFLIForNotes> = cloneDeep(this.selectedOptions);
        this.currentSelectedItems = Array.from(clonedSelectedOptionsMap.values());

        setTimeout(() => {
            this.filterCtrl.setValue(this.filterText);
            this.autoCompleteTrigger.openPanel();
        }, 0);
    }
}
